import PasswordStrength from '@fnando/password_strength/app/assets/javascripts/password_strength';
import { useMemo } from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';

import InputField from 'dashboard/components/FormControls/InputField';
import PasswordStrengthIndicator from 'dashboard/components/PasswordStrengthIndicator';
import Form from 'dashboard/layouts/FormLayout/Form';
import Section from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import {
  areValuesMatching,
  isMaxLength,
  isPasswordStrong,
  isRequired,
  withMessage,
} from 'lib/utils/formValidation';

const FORM_NAME = 'currentUserPassword';

interface FormCurrentUserPasswordProps {
  username: string;
  password: string;
}

const FormCurrentUserPassword = ({
  handleSubmit,
  submitting,
  pristine,
  username,
  password = '',
}: FormCurrentUserPasswordProps & InjectedFormProps) => {
  const validate = useMemo((): Partial<Record<keyof FormCurrentUserPasswordData, unknown>> => {
    return {
      password: [
        withMessage('Please enter a new password', isRequired),
        withMessage(
          'Provided passwords must match',
          areValuesMatching(['password', 'passwordConfirmation']),
        ),
        withMessage(
          'A strong password is required. Please try a mix of letters, numbers, and symbols.',
          isPasswordStrong(username),
        ),
        withMessage('Please limit password to 50 characters.', isMaxLength(50)),
      ],
      currentPassword: [withMessage('Please enter your current password', isRequired)],
    };
  }, [username]);

  let strength: number | null = null;
  if (Boolean(username) && Boolean(password)) {
    strength = PasswordStrength.test(username, password).score;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Section cols={2} colsMobile={1}>
        <Field
          component={InputField}
          name='currentPassword'
          id='currentPassword'
          type='password'
          validate={validate.currentPassword}
          disabled={submitting}
          placeholder='Current password'
          label='Current password'
        />
      </Section>
      <Section cols={2} colsMobile={1}>
        <div>
          <Field
            component={InputField}
            name='password'
            id='password'
            type='password'
            validate={validate.password}
            disabled={submitting}
            placeholder='New password'
            label='New password'
          />
          <PasswordStrengthIndicator strength={strength} />
        </div>
        <Field
          component={InputField}
          name='passwordConfirmation'
          id='passwordConfirmation'
          type='password'
          validate={validate.password}
          disabled={submitting}
          placeholder='Confirm new password'
          label='Confirm new password'
        />
      </Section>
      <Section cols={3} colsMobile={1}>
        <Button
          type='submit'
          data-test='form-current-user-password__save'
          disabled={submitting || pristine}
          isPending={submitting}
        >
          Change password
        </Button>
      </Section>
    </Form>
  );
};

export interface FormCurrentUserPasswordData {
  currentPassword: string;
  password: string;
  passwordConfirmation: string;
}

const reduxFormWrapped = reduxForm<FormCurrentUserPasswordData, FormCurrentUserPasswordProps>({
  form: FORM_NAME,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormCurrentUserPassword);

// Connect to form state to grab the current value of `password`, which
// the component uses to determine password strength.
const selector = formValueSelector(FORM_NAME);
export default connect((state) => {
  return {
    password: selector(state, 'password') || '',
  };
})(reduxFormWrapped);
